/**
 * What farmers produce (the "What do you produce" form) and the farmers'
 * associations they can belong to (the first-time wizard).
 *
 * Items are the common crops, animals, fish and poultry farmed across
 * Africa, with the Nigerian name in brackets where it is the usual one.
 * Names stay in English in every language: they are what shops and
 * extension workers use. "Other" lets the farmer type a name.
 */

export const OTHER = 'Other';

// Test entries in the wizard's cluster list until real clusters are set
// up in /admin. Set to false to hide them.
export const SHOW_TEST_CLUSTERS = true;
export const TEST_CLUSTERS = Array.from({ length: 10 }, (_, i) => `Cluster ${i + 1}`);

export const PRODUCE_CATEGORIES = ['crops', 'animals', 'poultry', 'fish'];

export const PRODUCE_ITEMS = {
  crops: [
    // Grains
    'Maize', 'Rice', 'Sorghum (Guinea corn)', 'Millet', 'Finger millet', 'Wheat', 'Barley',
    'Teff', 'Fonio (Acha)',
    // Roots and tubers
    'Cassava', 'Yam', 'Cocoyam', 'Sweet potato', 'Irish potato',
    // Beans, nuts and oil seeds
    'Cowpea', 'Beans', 'Soybean', 'Groundnut', 'Bambara groundnut', 'Pigeon pea', 'Chickpea',
    'Sesame (Beniseed)', 'Sunflower', 'Oil palm', 'Coconut', 'Shea',
    // Vegetables and spices
    'Tomato', 'Pepper (chilli)', 'Sweet pepper (Tatashe)', 'Onion', 'Garlic', 'Ginger', 'Turmeric',
    'Okra', 'Cabbage', 'Carrot', 'Lettuce', 'Spinach', 'Amaranth (Efo tete)', 'Jute mallow (Ewedu)',
    'Fluted pumpkin (Ugu)', 'Waterleaf', 'Bitter leaf', 'Garden egg', 'Cucumber', 'Watermelon',
    'Pumpkin', 'Egusi (melon seed)',
    // Fruit and tree crops
    'Banana', 'Plantain', 'Pineapple', 'Mango', 'Orange / citrus', 'Pawpaw (papaya)', 'Guava',
    'Avocado', 'Cashew', 'Cocoa', 'Coffee', 'Tea', 'Kola nut',
    // Cash and fibre crops
    'Sugarcane', 'Tiger nut', 'Cotton', 'Tobacco', 'Rubber', 'Kenaf',
    OTHER,
  ],
  animals: [
    'Beef cattle', 'Dairy cattle', 'Goats', 'Sheep', 'Pigs', 'Rabbits', 'Grasscutter', 'Snails',
    'Bees (honey)', 'Donkeys', 'Camels', 'Horses',
    OTHER,
  ],
  poultry: [
    'Broiler chickens', 'Layer chickens (eggs)', 'Local chickens', 'Noiler chickens', 'Cockerels',
    'Turkeys', 'Ducks', 'Guinea fowl', 'Geese', 'Quails', 'Pigeons', 'Ostriches',
    OTHER,
  ],
  fish: [
    'Catfish', 'Tilapia', 'Heterotis', 'Carp', 'Nile perch', 'Snakehead', 'Freshwater prawns',
    'Crayfish', 'Ornamental fish',
    OTHER,
  ],
};

/** The category of an existing plot; rows from before this form are crops. */
export function categoryOf(plot) {
  return plot?.produce_category || 'crops';
}

export const NIGERIAN_FARMER_ASSOCIATIONS = [
  'All Farmers Association of Nigeria (AFAN)',
  'Federation of Agricultural Commodity Associations of Nigeria (FACAN)',
  'Rice Farmers Association of Nigeria (RIFAN)',
  'Maize Association of Nigeria (MAAN)',
  'Soya Beans Farmers Association of Nigeria (SOFAN)',
  'National Cotton Association of Nigeria (NACOTAN)',
  'Cotton Producers and Merchants Association of Nigeria (COPMAN)',
  "Nigeria Cassava Growers' Association (NCGA)",
  'National Association of Sorghum Producers, Processors and Marketers of Nigeria',
  'Cocoa Farmers Association of Nigeria (CFAN)',
  'Cocoa Growers Association of Nigeria (COGAN)',
  'Tomatoes and Orchard Producers Association of Nigeria (TOPAN)',
  'National Ginger Association of Nigeria',
  'Cocoyam Growers and Marketing Association of Nigeria (COGMAN)',
  'Yam Farmers, Processors and Marketers Association of Nigeria (YFMAN)',
  'Poultry Association of Nigeria (PAN)',
  'Catfish Farmers Association of Nigeria (CAFAN)',
  'Catfish and Allied Fish Farmers Association',
  'Miyetti Allah Cattle Breeders Association of Nigeria (MACBAN)',
  OTHER,
];

// Units a farmer sells produce in (Sell produce, PR 67). Same list as the API.
export const SELL_UNITS = ['kg', 'tonne', 'bag', 'basket', 'crate', 'bunch', 'tuber', 'litre', 'head', 'bird', 'piece', 'dozen'];
